import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import type { ReminderEntityConfig } from "web_myplann/Reminders/Config/reminderEntityConfig";
import { applyReminderTemplateFormValues } from "web_myplann/Reminders/Forms/applyReminderTemplateFormValues";
import { getReminderTemplateFormValues } from "web_myplann/Reminders/Forms/getReminderTemplateFormValues";
import {
	getReminderFormDefaultValues,
	getReminderFormSchema,
	type ReminderFormModel,
} from "web_myplann/Reminders/Forms/reminderFormSchema";
import type { ReminderTemplate } from "web_myplann/Reminders/Types/ReminderTypes";

type UseReminderFormProps = {
	entityConfig: ReminderEntityConfig;
	templates: ReminderTemplate[];
	validTo: Date | null | undefined;
	initialModel?: ReminderFormModel;
};

export const useReminderForm = ({
	entityConfig,
	templates,
	validTo,
	initialModel,
}: UseReminderFormProps) => {
	const { t } = useResource();

	const initialDate = initialModel?.date ?? null;

	const resolver = useMemo(
		() => zodResolver(getReminderFormSchema(t, initialDate, entityConfig)),
		[t, initialDate, entityConfig],
	);

	const form = useForm<ReminderFormModel>({
		resolver,
		mode: "onChange",
		defaultValues: initialModel ?? getReminderFormDefaultValues(entityConfig),
	});

	const { setValue, getValues } = form;

	const customTypeKey = entityConfig.allowCustom
		? templates.find((template) => template.isCustom)?.typeKey
		: undefined;
	const firstTemplate = entityConfig.allowCustom ? undefined : templates[0];

	useEffect(() => {
		if (customTypeKey === undefined || getValues("typeKey") !== "") {
			return;
		}

		setValue("typeKey", customTypeKey, { shouldValidate: true });
	}, [customTypeKey, getValues, setValue]);

	useEffect(() => {
		if (firstTemplate === undefined || getValues("typeKey") !== "") {
			return;
		}

		applyReminderTemplateFormValues(
			setValue,
			getReminderTemplateFormValues(firstTemplate, entityConfig, validTo, t),
			{ shouldValidate: true },
		);
	}, [firstTemplate, entityConfig, validTo, t, getValues, setValue]);

	return { form };
};
